import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import {
  formatDateRu,
  formatTimeRu,
  toDateInputValue,
} from "../../../utils/appointmentLabels";
import StatusBadge from "../ui/StatusBadge";
import PatientRescheduleModal from "../PatientPortal/PatientRescheduleModal";
import L from "../../../constants/baysportLabels";
import s from "../clinic.module.css";
import {
  Button,
  Card,
  EmptyState,
  Field,
  Textarea,
  useToast,
  IconCalendar,
} from "../../ui";

function trainerName(appointment) {
  return (
    appointment.doctor?.full_name ||
    appointment.trainer?.full_name ||
    appointment.Doctor?.full_name ||
    "Тренер"
  );
}

function serviceName(appointment) {
  return (
    appointment.service?.name ||
    appointment.Service?.name ||
    "Занятие"
  );
}

function ReviewBlock({ appointment, onDone }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (rating < 1) {
      setErr("Выберите оценку от 1 до 5");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await clinicAPI.addDoctorReview(appointment.doctor_id, {
        rating,
        comment,
        appointment_id: appointment.id,
      });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.error || "Не удалось отправить отзыв");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={s.reviewForm}>
      <p className={s.reviewFormHint}>
        Оцените занятие — отзыв поможет другим {L.clients.toLowerCase()}
      </p>
      <div className={s.reviewStarsRow}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className={`${s.reviewStarBtn} ${n <= rating ? s.reviewStarBtnOn : ""}`}
            onClick={() => setRating(n)}
            aria-label={`${n} звёзд`}
          >
            ★
          </button>
        ))}
      </div>
      <Field label="Комментарий (необязательно)">
        <Textarea
          rows={2}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Field>
      {err ? <p className={s.alertError}>{err}</p> : null}
      <Button type="button" size="sm" onClick={submit} disabled={saving}>
        {saving ? "Отправка…" : "Оставить отзыв"}
      </Button>
    </div>
  );
}

function VisitDetails({ appointment }) {
  const branch = appointment.branch;
  return (
    <div className={s.portalVisitDetails}>
      {branch?.address ? (
        <p>
          <strong>Адрес:</strong> {branch.address}
          {branch.metro ? ` (м. ${branch.metro})` : ""}
        </p>
      ) : null}
      {branch?.phone ? (
        <p>
          <strong>Телефон:</strong> {branch.phone}
        </p>
      ) : null}
      {appointment.service?.description ? (
        <p>
          <strong>Подготовка:</strong> {appointment.service.description}
        </p>
      ) : (
        <p className={s.portalDocMeta}>
          Приходите за 10–15 минут до начала. Возьмите спортивную форму и воду.
        </p>
      )}
      {appointment.notes ? (
        <p>
          <strong>Примечание:</strong> {appointment.notes}
        </p>
      ) : null}
    </div>
  );
}

function VisitCardApp({
  appointment,
  showReview,
  onCancel,
  onReviewDone,
  onReschedule,
  expanded,
  onToggle,
}) {
  const canManage = ["scheduled", "confirmed"].includes(appointment.status);
  const dt = new Date(appointment.start_datetime);
  const isToday = toDateInputValue(dt) === toDateInputValue(new Date());

  return (
    <article className={s.clientVisitCard}>
      <div className={s.clientVisitCardTop}>
        <div className={s.clientVisitWhen}>
          <span className={s.clientVisitTime}>{formatTimeRu(dt)}</span>
          <span className={s.clientVisitDate}>
            {isToday
              ? "Сегодня"
              : formatDateRu(dt, { weekday: "short", day: "numeric", month: "short" })}
          </span>
        </div>
        <StatusBadge status={appointment.status} />
      </div>

      <div className={s.clientVisitBody}>
        <div className={s.clientVisitTitle}>{serviceName(appointment)}</div>
        <div className={s.clientVisitMeta}>{trainerName(appointment)}</div>
        {appointment.branch?.name ? (
          <div className={s.clientVisitMeta}>{appointment.branch.name}</div>
        ) : null}
      </div>

      {expanded ? <VisitDetails appointment={appointment} /> : null}

      {showReview && appointment.can_review ? (
        <ReviewBlock appointment={appointment} onDone={onReviewDone} />
      ) : null}
      {showReview && appointment.has_review ? (
        <p className={s.reviewDoneHint}>Спасибо за отзыв</p>
      ) : null}

      <div className={s.clientVisitActions}>
        <Button type="button" variant="ghost" size="sm" onClick={onToggle}>
          {expanded ? "Скрыть" : "Подробнее"}
        </Button>
        {canManage ? (
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onReschedule(appointment)}
            >
              Перенести
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onCancel(appointment.id)}
            >
              Отменить
            </Button>
          </>
        ) : null}
      </div>
    </article>
  );
}

function VisitCardDesktop({
  appointment,
  showReview,
  onCancel,
  onReviewDone,
  onReschedule,
  expanded,
  onToggle,
}) {
  const canManage = ["scheduled", "confirmed"].includes(appointment.status);

  return (
    <div className={s.apptCard}>
      <div className={s.apptCardBody}>
        <div className={s.apptDate}>
          {formatDateRu(appointment.start_datetime, { weekday: "long" })} в{" "}
          {formatTimeRu(appointment.start_datetime)}
        </div>
        <div className={s.apptMeta}>
          {trainerName(appointment)} · {serviceName(appointment)}
        </div>
        {appointment.branch?.name ? (
          <div className={s.apptMeta}>{appointment.branch.name}</div>
        ) : null}
        {expanded ? <VisitDetails appointment={appointment} /> : null}
        {showReview && appointment.can_review ? (
          <ReviewBlock appointment={appointment} onDone={onReviewDone} />
        ) : null}
        {showReview && appointment.has_review ? (
          <p className={s.reviewDoneHint}>Спасибо за отзыв</p>
        ) : null}
      </div>
      <div className={s.apptCardActionsCol}>
        <StatusBadge status={appointment.status} />
        <div className={s.apptCardActions}>
          <Button type="button" variant="ghost" size="sm" onClick={onToggle}>
            {expanded ? "Скрыть" : "Детали"}
          </Button>
          {canManage ? (
            <>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onReschedule(appointment)}
              >
                Перенести
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onCancel(appointment.id)}
              >
                Отменить
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function VisitsSection({
  embedded,
  title,
  subtitle,
  list,
  emptyTitle,
  emptyDesc,
  showReview,
  loading,
  expandedId,
  onToggle,
  onCancel,
  onReviewDone,
  onReschedule,
  cta,
}) {
  if (embedded) {
    return (
      <section className={s.clientVisitsSection}>
        <header className={s.clientVisitsSectionHead}>
          <h2 className={s.clientVisitsSectionTitle}>{title}</h2>
          <p className={s.clientVisitsSectionSub}>{subtitle}</p>
        </header>
        {loading ? (
          <p className={s.timelineLoading}>Загрузка занятий…</p>
        ) : list.length === 0 ? (
          <EmptyState icon={IconCalendar} title={emptyTitle} description={emptyDesc} />
        ) : (
          <div className={s.clientVisitList}>
            {list.map((a) => (
              <VisitCardApp
                key={a.id}
                appointment={a}
                showReview={showReview}
                onCancel={onCancel}
                onReviewDone={onReviewDone}
                onReschedule={onReschedule}
                expanded={expandedId === a.id}
                onToggle={() => onToggle(a.id)}
              />
            ))}
          </div>
        )}
        {cta}
      </section>
    );
  }

  return (
    <Card title={title} subtitle={subtitle}>
      {loading ? (
        <p className={s.timelineLoading}>Загрузка занятий…</p>
      ) : list.length === 0 ? (
        <EmptyState icon={IconCalendar} title={emptyTitle} description={emptyDesc} />
      ) : (
        <div className={s.apptList}>
          {list.map((a) => (
            <VisitCardDesktop
              key={a.id}
              appointment={a}
              showReview={showReview}
              onCancel={onCancel}
              onReviewDone={onReviewDone}
              onReschedule={onReschedule}
              expanded={expandedId === a.id}
              onToggle={() => onToggle(a.id)}
            />
          ))}
        </div>
      )}
      {cta}
    </Card>
  );
}

export default function PatientVisits({ embedded = false, appMode = false } = {}) {
  const toast = useToast();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);

  const load = () => {
    setLoading(true);
    clinicAPI
      .getAppointments()
      .then((r) => setAppointments(r.appointments || []))
      .catch((e) =>
        toast.error(e.response?.data?.error || "Ошибка загрузки"),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const cancel = async (id) => {
    if (!window.confirm("Отменить запись?")) return;
    try {
      await clinicAPI.cancelAppointment(id);
      toast.success("Запись отменена");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка отмены");
    }
  };

  const upcoming = appointments.filter(
    (a) =>
      a.status !== "cancelled" &&
      a.status !== "completed" &&
      new Date(a.start_datetime) >= new Date(),
  );
  const past = appointments.filter(
    (a) =>
      a.status === "completed" ||
      a.status === "cancelled" ||
      new Date(a.start_datetime) < new Date(),
  );

  const content = (
    <div className={embedded && appMode ? s.clientVisits : s.portalVisits}>
      <VisitsSection
        embedded={embedded && appMode}
        title="Предстоящие"
        subtitle={`${upcoming.length} ${upcoming.length === 1 ? "запись" : "записей"}`}
        list={upcoming}
        emptyTitle="Нет предстоящих занятий"
        emptyDesc={`Запишитесь на тренировку — выберите ${L.trainer.toLowerCase()} и удобное время`}
        showReview={false}
        loading={loading}
        expandedId={expandedId}
        onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
        onCancel={cancel}
        onReviewDone={load}
        onReschedule={setRescheduleAppt}
        cta={
          !loading && upcoming.length === 0 ? (
            <div className={s.clientVisitsCta}>
              <Link to="/app/book">
                <Button>Записаться</Button>
              </Link>
            </div>
          ) : null
        }
      />

      <VisitsSection
        embedded={embedded && appMode}
        title="История"
        subtitle="Завершённые и отменённые"
        list={past}
        emptyTitle="История пуста"
        emptyDesc="Здесь появятся прошедшие тренировки"
        showReview
        loading={loading}
        expandedId={expandedId}
        onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
        onCancel={cancel}
        onReviewDone={load}
        onReschedule={setRescheduleAppt}
      />

      <PatientRescheduleModal
        appointment={rescheduleAppt}
        open={!!rescheduleAppt}
        onClose={() => setRescheduleAppt(null)}
        onDone={load}
      />
    </div>
  );

  return embedded ? (
    <div className={appMode ? s.clientVisitsPage : s.patientVisitsPage}>{content}</div>
  ) : (
    content
  );
}
